import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import axios from 'axios';

import { organisationFeaturesQueryKey } from 'app/hooks/useOrganisationFeatures';
import { axiosPrivate } from 'app/utils';

export interface PlusUpgradeRequestResult {
  /** Account management already had a request from this organisation today. */
  alreadyRequested: boolean;
}

// The request changes nothing on the organisation: it alerts account management, who move it to
// Plus in PAM.
async function requestPlusUpgrade(organisationId: string): Promise<PlusUpgradeRequestResult> {
  try {
    await axiosPrivate.post(`/v3/pharmacist/organisations/${organisationId}/request-plus-upgrade`);
    return { alreadyRequested: false };
  } catch (error) {
    // The API takes one request per organisation per day; a second one is already with account
    // management, so to the pharmacy it is as good as sent.
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      return { alreadyRequested: true };
    }
    throw error;
  }
}

export default function useRequestPlusUpgrade(
  organisationId: string
): UseMutationResult<PlusUpgradeRequestResult, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!organisationId) return Promise.reject(new Error('Missing organisation ID'));
      return requestPlusUpgrade(organisationId);
    },
    onSuccess: () => {
      // Every upgrade button reads the request from the organisation features. Not awaited, so the
      // modal confirms the request before the buttons behind it turn into "Upgrade requested".
      queryClient.invalidateQueries({ queryKey: organisationFeaturesQueryKey(organisationId) });
    },
    meta: {
      errorMessage: 'Failed to send your upgrade request',
    },
  });
}
